import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PartnersSlider from './PartnersSlider';
import PopularProducts from './PopularProducts';
import FilterSection from './FilterSection';
import ProductCard from '../../../components/ProductCard';
import NoticeComponent from '../../../components/NoticeComponent';
import { Product } from '../../../types/types';
import { instance } from "../../../api/axiosInstance";

const ITEMS_PER_PAGE = 40; // 한 페이지에 표시할 상품 개수
const MAX_PAGE_BUTTONS = 5; // 페이지네이션에 표시할 최대 페이지 버튼 수

const formatNumber = (number: number | bigint) => {
  return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 3 }).format(number);
};

// PaginationProps 인터페이스 정의
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

// 페이지네이션 컴포넌트
const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange }) => {
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const half = Math.floor(MAX_PAGE_BUTTONS / 2);
    let start = Math.max(2, currentPage - half);
    let end = Math.min(totalPages - 1, currentPage + half);

    if (currentPage - 1 <= half) {
      start = 2;
      end = Math.min(totalPages - 1, MAX_PAGE_BUTTONS);
    }

    if (totalPages - currentPage <= half) {
      start = Math.max(2, totalPages - MAX_PAGE_BUTTONS + 1);
      end = totalPages - 1;
    }

    pages.push(1);

    if (start > 2) {
      pages.push('left-ellipsis');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 1) {
      pages.push('right-ellipsis');
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  const pages = getPageNumbers();

  return (
    <nav className="inline-flex -space-x-px" aria-label="Pagination Navigation">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`px-3 py-2 ml-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-l-lg hover:bg-gray-100 hover:text-gray-700 ${
          currentPage === 1 ? 'cursor-not-allowed opacity-50' : ''
        }`}
        aria-label="Previous Page"
      >
        &laquo;
      </button>

      {pages.map((page, index) => {
        if (page === 'left-ellipsis' || page === 'right-ellipsis') {
          return (
            <span key={index} className="px-3 py-2 leading-tight text-gray-500 bg-white border border-gray-300">
              &hellip;
            </span>
          );
        }

        return (
          <button
            key={page}
            onClick={() => onPageChange(page as number)}
            className={`px-3 py-2 leading-tight border border-gray-300 hover:bg-gray-100 hover:text-gray-700 ${
              page === currentPage
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-500 bg-white'
            }`}
            aria-current={page === currentPage ? 'page' : undefined}
          >
            {page}
          </button>
        );
      })}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`px-3 py-2 leading-tight text-gray-500 bg-white border border-gray-300 rounded-r-lg hover:bg-gray-100 hover:text-gray-700 ${
          currentPage === totalPages ? 'cursor-not-allowed opacity-50' : ''
        }`}
        aria-label="Next Page"
      >
        &raquo;
      </button>
    </nav>
  );
};

export default function ModelingProduct() {
  const { t } = useTranslation();
  const { category } = useParams<{ category: string }>();

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0); // 총 상품 개수
  const [currentPage, setCurrentPage] = useState<number>(1); // 현재 페이지
  const [totalPages, setTotalPages] = useState<number>(1); // 총 페이지 수

  // 필터 상태
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 200000]);
  const [isDiscounted, setIsDiscounted] = useState<boolean | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [starRating, setStarRating] = useState<number>(1);
  const [selectedHashtags, setSelectedHashtags] = useState<string[]>([]);

  // 상품 데이터 가져오기
  const fetchProducts = async (page: number) => {
    setLoading(true);
    try {
      const params = {
        start: (page - 1) * ITEMS_PER_PAGE,
        count: ITEMS_PER_PAGE,
        category,
        isDiscounted,
        minPrice: priceRange[0],
        maxPrice: priceRange[1],
        sortBy: 'idDesc', // 기본 정렬 기준 설정 (필요시 변경)
        // 추가 필터링 파라미터가 필요하면 여기에 추가
      };

      const response = await instance.get('/api/products/filter', { params });
      console.log('API Response:', response.data);

      const fetchedProducts: Product[] = response.data;

      setProducts(fetchedProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]); // 에러 발생 시 상품 리스트 초기화
    } finally {
      setLoading(false);
    }
  };

  // 총 상품 개수 가져오기
  const fetchTotalCount = async () => {
    try {
      const response = await instance.get('/api/products/countByCategory', {
        params: { category },
      });
      console.log('Total Count Response:', response.data);
      const totalCount: number = response.data;

      setTotal(totalCount);
      setTotalPages(Math.ceil(totalCount / ITEMS_PER_PAGE));
    } catch (error) {
      console.error('Error fetching total count:', error);
      setTotal(0);
      setTotalPages(1);
    }
  };

  // 필터 변경 시 초기화 및 데이터 재호출
  useEffect(() => {
    if (!category) return;

    // 총 개수와 첫 페이지 데이터 가져오기
    const initializeData = async () => {
      await fetchTotalCount();
      setCurrentPage(1);
      await fetchProducts(1);
    };

    initializeData();
  }, [category, isDiscounted, priceRange, searchTerm, starRating, selectedHashtags]);

  // 페이지 변경 시 데이터 가져오기
  useEffect(() => {
    if (!category) return;

    fetchProducts(currentPage);

    // 페이지 변경 시 스크롤을 상단으로 이동
    window.scrollTo({
      top: 0,
      behavior: 'smooth', // 부드러운 스크롤
    });
  }, [currentPage, category]);

  // 페이지네이션 핸들러
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  if (!category) {
    return <Navigate to="/error" />;
  }

  return (
    <section className="max-w-[2560px] w-full mx-auto mt-[130px] mobile:mt-[130px] mobile:px-4 mobile:overflow-hidden">
      <div className=" max-w-[1440px] w-full mx-auto">
        {/* <NoticeComponent
          title={t(`product.title.${category}`)}
          content={t('product.messages.category', {
            category: t(`product.title.${category}`),
          })}
        />

        <PartnersSlider /> */}

        <PopularProducts formatNumber={formatNumber} category={category} />
      </div>

      <div className="mt-[58px] mobile:mt-[38px] max-w-[1440px] w-full mx-auto relative">
        <div className="text-left">
          <h2 className="flex items-center text-3xl text-[#000] font-bold mobile:text-lg mobile:font-semibold">
            {t(`product.title.${category}`)}
            <span className="font-light text-[22px] ml-2 text-[#969696] mobile:text-xs">
              ({total})
            </span>
          </h2>
          {/* <p className="font-light text-sm text-[#969696] leading-6">
            {t(`product.description.${category}`)}
          </p> */}
        </div>

        <article className="pb-[68px] mt-[28px] flex gap-7 mobile:flex-col mobile:mt-0">
          {/* 필터 섹션 */}
          <div className="w-[22%] mobile:w-full">
            <div className="sticky top-[10%] mobile:z-[999999999]">
              <FilterSection
                onPriceRangeChange={setPriceRange}
                onDiscountChange={setIsDiscounted}
                onSearchTermChange={setSearchTerm}
                onStarRatingChange={setStarRating}
                onHashtagChange={setSelectedHashtags}
                category={category}
              />
            </div>
          </div>

          {/* 상품 목록 */}
          <div className="w-4/5 mobile:w-full">
            {loading && products.length === 0 ? (
              <div className="text-center text-gray-500">{t('loading')}</div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-4 gap-4 mobile:gap-[6px] mobile:grid-cols-2 mobile:z-0">
                {products.map((product: Product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    formatNumber={formatNumber}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500">
                {t('product.messages.no_products', '상품이 없습니다.')}
              </div>
            )}

            {/* 페이지네이션 섹션 */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-8">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </article>

       
      </div>
    </section>
  );
}
